'use client';

import Link from 'next/link';
import OrderList from '@/components/account/order-list';
import LogoutButton from '@/components/account/logout-button';
import { useAuth } from '@/context/auth-context';

export default function AccountPageClient() {
  const { customer, isLoading } = useAuth();
  const recentOrders = customer?.orders.slice(0, 5) ?? [];

  return (
    <section className="account-page">
      <div className="container">
        {isLoading ? (
          <div className="account-section">
            <p className="account-loading">Loading your account...</p>
          </div>
        ) : customer ? (
          <>
            <header className="account-header">
              <div>
                <h1 className="account-title">
                  Welcome back{customer.firstName ? `, ${customer.firstName}` : ''}
                </h1>
                <p className="account-email">{customer.email}</p>
              </div>
              <LogoutButton />
            </header>

            <div className="account-section">
              <div className="account-section-head">
                <h2 className="account-section-title">Recent orders</h2>
                {customer.orders.length > 5 && (
                  <Link href="/track-order" className="account-section-link">
                    View all orders -&gt;
                  </Link>
                )}
              </div>
              <OrderList orders={recentOrders} />
            </div>
          </>
        ) : (
          <div className="account-section account-empty">
            <h1 className="account-title">My account</h1>
            <p className="account-email">
              Sign in to view recent orders and track your Bathroomfort purchases.
            </p>
            <div className="account-actions">
              <Link href="/account/login?next=/account" className="account-primary-link">
                Sign in
              </Link>
              <Link href="/account/register" className="account-secondary-link">
                Create account
              </Link>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .account-page {
          padding: 2rem 0 4rem;
          background: var(--color-off-white);
          min-height: 60vh;
        }
        .account-page .container { max-width: 920px; }
        .account-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 1.5rem 1.75rem;
          margin-bottom: 1.5rem;
        }
        .account-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 0.25rem;
        }
        .account-email,
        .account-loading {
          font-size: 0.9rem;
          color: var(--color-text-secondary);
          margin: 0;
        }
        .account-section {
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 1.5rem 1.75rem;
        }
        .account-empty {
          text-align: center;
          max-width: 520px;
          margin: 0 auto;
        }
        .account-section-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        .account-section-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0;
        }
        .account-section-link {
          font-size: 0.85rem;
          color: var(--color-primary);
          text-decoration: none;
          font-weight: 600;
        }
        .account-section-link:hover { text-decoration: underline; }
        .account-actions {
          display: flex;
          justify-content: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-top: 1.5rem;
        }
        .account-primary-link,
        .account-secondary-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 2.75rem;
          padding: 0.75rem 1.2rem;
          border-radius: 1rem;
          font-size: 0.9rem;
          font-weight: 700;
          text-decoration: none;
        }
        .account-primary-link {
          background: var(--color-primary);
          color: var(--color-white);
        }
        .account-primary-link:hover { background: var(--color-primary-dark); }
        .account-secondary-link {
          background: var(--color-white);
          color: var(--color-primary);
          border: 1px solid var(--color-border);
        }
        .account-secondary-link:hover { border-color: var(--color-primary); }
      `}</style>
    </section>
  );
}
